/* eslint-disable eqeqeq */

import React, {
  CSSProperties,
  ComponentProps,
  FC,
  useCallback,
  useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ListItem from 'components/ListItem';
import {
  deletePayment,
  setPayment,
  setPaymentDecimalEdit,
  setPaymentEditIndex,
  setPaymentSelected,
  updateCurrency,
} from 'actions/Payments';
import {
  getPaymentDecimalEdit,
  getPaymentEditIndex,
  getPayments,
  getPaymentsCurrency,
  getPaymentSelected,
} from 'reducers/Payments';
import {
  getDefaultCurrency,
  getIsModuleEnabled,
} from 'reducers/configs/settings';
import { getCurrencyByCode } from 'reducers/configs/currency';
import { range } from 'utils/tsHelpers';

interface ButtonItemProps extends ComponentProps<typeof ListItem> {
  style?: CSSProperties;
}

const ButtonItem: FC<ButtonItemProps> = ({ style, ...props }) => (
  <ListItem
    style={{
      fontSize: 24,
      fontFamily: 'Proxima Nova',
      minWidth: 80,
      minHeight: 76,
      height: 'inherit',
      width: 'inherit',
      ...style,
    }}
    {...props}
  />
);

const NumberPad: FC = () => {
  const dispatch = useDispatch();
  const payments = useSelector(getPayments);
  const paymentSelected = useSelector(getPaymentSelected);
  const { code: selectedCurrency } = useSelector(getPaymentsCurrency);
  const defaultCurrencyCode = useSelector(getDefaultCurrency);
  const defaultCurrency = useSelector(getCurrencyByCode(defaultCurrencyCode));
  const paymentEditIndex = useSelector(getPaymentEditIndex);
  const paymentDecimalEdit = useSelector(getPaymentDecimalEdit);
  const isMultiCurrencyEnabled = useSelector(state =>
    getIsModuleEnabled('pos_multicurrency')(state),
  );

  const area = n => ({
    gridArea: n,
    alignSelf: 'stretch',
    justifySelf: 'stretch',
  });
  const containsDigitsOnly = value => /^\d+$/.test(value);
  const containsSeparator = value => /^[.,]+$/.test(value);
  const containsTerminator = value => /\b(?:Backspace|Delete)\b/.test(value);
  const containsEnter = value => /\b(?:Enter)\b/.test(value);

  const setCharAt = (str, index, chr) => {
    if (index > str.length - 1) return str + chr;
    return str.substr(0, index) + chr + str.substr(index + 1);
  };

  // prettier-ignore
  const keyBinds = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", ".", ",", "Backspace", "Enter", "Delete"];

  const processKey = useCallback(
    async key => {
      const newKey =
        paymentSelected === 'cash'
          ? `${selectedCurrency}-${paymentSelected}`
          : `${paymentSelected}`;
      const selectedPaymentObject =
        payments[newKey] ?? payments[paymentSelected];

      if (containsDigitsOnly(key)) {
        const oldPayment = selectedPaymentObject?.amount ?? '0.00';
        const oldInteger = oldPayment.split('.')[0].replace('-', '');
        const oldDecimal = oldPayment.split('.')[1];
        const currentIndex =
          oldPayment == '0.00' && !paymentDecimalEdit ? 0 : paymentEditIndex;
        const newInteger = paymentDecimalEdit
          ? oldInteger
          : setCharAt(oldInteger, currentIndex, key);

        const newDecimal =
          paymentDecimalEdit && currentIndex < 2
            ? setCharAt(oldDecimal, currentIndex, key)
            : oldDecimal;

        const newAmount = `${newInteger}.${newDecimal}`;

        await dispatch(
          setPaymentEditIndex(
            currentIndex === 0 && key == '0' && !paymentDecimalEdit
              ? currentIndex
              : currentIndex + 1,
          ),
        );
        await dispatch(
          setPayment({
            ...selectedPaymentObject,
            amount: newAmount,
            key: newKey,
          }),
        );
      } else if (containsSeparator(key) && !paymentDecimalEdit) {
        await dispatch(setPaymentDecimalEdit(true));
        await dispatch(setPaymentEditIndex(0));
      } else if (containsTerminator(key)) {
        await dispatch(
          setPayment({
            ...selectedPaymentObject,
            amount: '0.00',
            key: newKey,
          }),
        );
        await dispatch(setPaymentDecimalEdit(false));
        await dispatch(setPaymentEditIndex(0));
      } else if (containsEnter(key)) {
        await dispatch(setPaymentSelected(''));
        if (selectedPaymentObject.amount === '0.00')
          dispatch(deletePayment({ key: selectedPaymentObject.key ?? newKey }));
        await dispatch(setPaymentDecimalEdit(false));
        await dispatch(setPaymentEditIndex(0));
      }
    },
    [
      paymentDecimalEdit,
      deletePayment,
      paymentEditIndex,
      paymentSelected,
      payments,
      setPaymentSelected,
      setPayment,
      dispatch,
      setPaymentDecimalEdit,
      setPaymentEditIndex,
    ],
  );

  const keyDownHandler = useCallback(
    event => {
      const { key } = event;

      if (keyBinds.includes(key)) {
        processKey(key);
      }
    },
    [keyBinds, processKey],
  );
  const clickHandler = useCallback(
    event => {
      const { value } = event.target;
      if (value === 'Enter') {
        dispatch(updateCurrency(defaultCurrency));
      }
      processKey(value);
    },
    [processKey],
  );

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);
    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [keyDownHandler]);

  return (
    <div
      style={{
        display: 'inline-grid',
        gridTemplateAreas: `'n1  n2  n3 '  ' n4  n5  n6 ' ' n7  n8  n9 ' ' dec  n0  ok '`,
      }}
      data-testid="payment-numberpad"
    >
      <ButtonItem
        value="."
        variant="grid_dark"
        style={{ ...area('dec'), fontSize: 24 }}
        action={clickHandler}
        data-testid="decimal-button"
        data-test-key="."
      >
        .
      </ButtonItem>
      <ButtonItem
        variant="grid_green"
        style={{ ...area('ok'), fontSize: 24, fontWeight: 700 }}
        value="Enter"
        action={clickHandler}
        data-testid="enter-button"
        data-test-key="OK"
      >
        OK
      </ButtonItem>

      {range(9).map(n => (
        <ButtonItem
          key={n}
          variant="grid_light"
          data-test-key={n}
          data-testid="numpad-button"
          value={n}
          style={area(`n${n}`)}
          action={clickHandler}
        >
          {n}
        </ButtonItem>
      ))}
    </div>
  );
};

export default NumberPad;
